import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import {
  ACTION_POPUP_CONTENT_CLASS,
  ACTION_POPUP_CONTENT_INNER_CLASS,
} from '../../../components/popup/popupLayoutSpec';
import Heading from '../../../components/common/Typography/Heading';
import { disableScroll, enableScroll } from '../../../utils/scrollUtils';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  canManagePermissionsAccess,
  canViewPermissionsAccess,
} from '../../../data/teamConfig';
import {
  formatPermissionDateDisplay,
  formatPermissionDateOnly,
  PERMISSIONS_ACCESS_ROUTE,
} from '../../../data/permissionsConfig';
import {
  buildSamplePermissionConfigs,
  formatConfigModuleColumnParts,
  getConfigPermissionPreviewParts,
} from '../../../data/permissionsAccessMatrix';

const tableCols =
  'minmax(88px, 0.55fr) minmax(140px, 1.1fr) minmax(88px, 0.65fr) minmax(110px, 0.85fr) minmax(120px, 0.9fr) minmax(200px, 1.4fr) minmax(88px, 0.55fr) minmax(96px, 0.6fr) minmax(120px, 0.85fr) minmax(48px, 0.2fr)';

const TABLE_MIN_WIDTH = '1480px';
/** Same preview pattern as Keywords Screen 1. */
const LIST_PREVIEW_COUNT = 1;

const PREVIEW_TABLE_COLS = 'minmax(92px, 0.45fr) minmax(0, 1fr)';

const ViewAllModal = ({ title, idLabel = '#', nameLabel = 'Name', rows, onClose }) => {
  useEffect(() => {
    disableScroll();
    return () => enableScroll();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="permissions-view-all-title"
    >
      <div className="flex-shrink-0 flex items-center justify-end h-[60px] sm:h-[80px] pr-4 sm:pr-6 z-10">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center text-offblack"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm cursor-pointer" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white rounded-t-xl min-h-0">
        <div
          className="overflow-auto relative flex items-center justify-center"
          style={{ minHeight: 'calc(100vh - 120px)' }}
        >
          <div className={`${ACTION_POPUP_CONTENT_CLASS} pb-10`}>
            <div id="permissions-view-all-title">
              <Heading
                variant="h6"
                className="flex items-center justify-center text-center text-offblack"
              >
                {title}
              </Heading>
            </div>
            <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
              <div
                className="grid w-full py-3 text-offblack border-b border-lightgray gap-x-3"
                style={{ gridTemplateColumns: PREVIEW_TABLE_COLS }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader className="truncate" title={idLabel}>
                    {idLabel}
                  </AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader className="truncate" title={nameLabel}>
                    {nameLabel}
                  </AdminTableHeader>
                </div>
              </div>
              {(rows || []).length === 0 ? (
                <div className="py-6 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No items to display.
                  </AdminSupportingText>
                </div>
              ) : (
                (rows || []).map((row, index) => (
                  <div
                    key={`${row.id}-${index}`}
                    className="grid w-full py-3 text-offblack border-b border-lightgray last:border-b-0 gap-x-3"
                    style={{ gridTemplateColumns: PREVIEW_TABLE_COLS }}
                  >
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableMeta className="truncate block" title={row.id}>
                        {row.id}
                      </AdminTableMeta>
                    </div>
                    <div className="flex items-center min-w-0 overflow-hidden">
                      <AdminTableBody className="break-words block" title={row.name}>
                        {row.name}
                      </AdminTableBody>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewListCell = ({ items, onViewAll }) => {
  if (!items || items.length === 0) {
    return (
      <AdminTableBody className="truncate block" title="—">
        —
      </AdminTableBody>
    );
  }
  const preview = items.slice(0, LIST_PREVIEW_COUNT).join(' · ');
  const extraCount = items.length - LIST_PREVIEW_COUNT;
  const fullText = items.join(' · ');
  return (
    <AdminTableBody className="block min-w-0 w-full" title={fullText}>
      <span className="flex items-center min-w-0 w-full gap-1">
        <span className="min-w-0 truncate">{preview}</span>
        {extraCount > 0 ? (
          <button
            type="button"
            className="text-primaryblue hover:underline shrink-0 whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              onViewAll?.();
            }}
          >
            +{extraCount} more
          </button>
        ) : null}
      </span>
    </AdminTableBody>
  );
};

const PermissionsAccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const canView = canViewPermissionsAccess(auth);
  const canManage = canManagePermissionsAccess(auth);

  const [configs, setConfigs] = useState(
    () => location.state?.configs || buildSamplePermissionConfigs(),
  );
  const [feedback, setFeedback] = useState(location.state?.feedback || '');
  const [viewAllPreview, setViewAllPreview] = useState(null);

  useEffect(() => {
    if (location.state?.configs) {
      setConfigs(location.state.configs);
    }
    if (location.state?.feedback) {
      setFeedback(location.state.feedback);
      navigate(location.pathname, { replace: true, state: { configs: location.state.configs } });
    }
  }, [location.state, location.pathname, navigate]);

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(configs);

  const openConfig = (config) => {
    navigate(`${PERMISSIONS_ACCESS_ROUTE}/config/${encodeURIComponent(config.id)}`, {
      state: { configs, config },
    });
  };

  const handleRowAction = (action, config) => {
    closeRowActions();
    if (action === 'View') {
      openConfig(config);
      return;
    }
    if (action === 'Edit' && canManage) {
      navigate(`${PERMISSIONS_ACCESS_ROUTE}/config/${encodeURIComponent(config.id)}/edit`, {
        state: { configs, config },
      });
    }
  };

  if (!canView) {
    return (
      <AdminLayout>
        <AdminPageTitle>Permissions &amp; Access</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to access Permissions &amp; Access.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Permissions &amp; Access</AdminPageTitle>
          {canManage && (
            <AdminHeaderAction onClick={() => navigate(`${PERMISSIONS_ACCESS_ROUTE}/add`, { state: { configs } })}>
              Add Rule
            </AdminHeaderAction>
          )}
        </div>

        {feedback ? (
          <AdminSupportingText className="text-offblack mb-4">{feedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div style={{ minWidth: TABLE_MIN_WIDTH }}>
              <div
                className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                style={{ gridTemplateColumns: tableCols }}
              >
                {[
                  'Permission ID',
                  'Module',
                  'Team Type',
                  'Department',
                  'Job Role',
                  'Permissions',
                  'Status',
                  'Date Added',
                  'Last Updated',
                  'Actions',
                ].map((label) => (
                  <div
                    key={label}
                    className={`flex items-center min-w-0 ${label === 'Actions' ? 'justify-center shrink-0' : ''} ${label === 'Status' ? 'shrink-0' : ''}`}
                  >
                    <AdminTableHeader className={label === 'Actions' || label === 'Status' ? '' : 'truncate'}>
                      {label}
                    </AdminTableHeader>
                  </div>
                ))}
              </div>

              {paginatedItems.length === 0 ? (
                <div className="py-10 px-4 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No Permission Configurations yet.
                  </AdminSupportingText>
                </div>
              ) : (
                paginatedItems.map((config) => {
                  const moduleParts = formatConfigModuleColumnParts(config);
                  const permissionParts = getConfigPermissionPreviewParts(config);
                  const actions = canManage ? ['View', 'Edit'] : ['View'];
                  return (
                    <div
                      key={config.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openConfig(config)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openConfig(config);
                        }
                      }}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                      style={{ gridTemplateColumns: tableCols }}
                    >
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block font-mono" title={config.id}>
                          {config.id}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <PreviewListCell
                          items={moduleParts.labels}
                          onViewAll={() =>
                            setViewAllPreview({
                              title: 'Modules',
                              idLabel: 'Module ID',
                              nameLabel: 'Module',
                              rows: moduleParts.previewRows,
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={config.teamType}>
                          {config.teamType}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={config.department}>
                          {config.department}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={config.jobRole}>
                          {config.jobRole}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <PreviewListCell
                          items={permissionParts.lines}
                          onViewAll={() =>
                            setViewAllPreview({
                              title: 'Permissions',
                              idLabel: 'Module ID',
                              nameLabel: 'Permission',
                              rows: permissionParts.previewRows,
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center min-w-0 shrink-0">
                        <AdminStatusBadge status={config.status} label={config.status} />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block whitespace-nowrap">
                          {formatPermissionDateOnly(config.dateAdded)}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block whitespace-nowrap">
                          {formatPermissionDateDisplay(config.lastUpdated)}
                        </AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center shrink-0 overflow-visible"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[config.id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(config.id);
                            }}
                            aria-expanded={!!rowActionsOpen[config.id]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[config.id] && rowActionsMenuLayout && (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              {actions.map((action) => (
                                <AdminRowMenuItem
                                  key={action}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowAction(action, config);
                                  }}
                                >
                                  {action}
                                </AdminRowMenuItem>
                              ))}
                            </AdminFixedRowActionsMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>

      {viewAllPreview ? (
        <ViewAllModal
          title={viewAllPreview.title}
          idLabel={viewAllPreview.idLabel}
          nameLabel={viewAllPreview.nameLabel}
          rows={viewAllPreview.rows}
          onClose={() => setViewAllPreview(null)}
        />
      ) : null}
    </AdminLayout>
  );
};

export default PermissionsAccess;
